import React from 'react'
import { useTranslation } from 'react-i18next'

interface WidgetPreviewConfig {
  position?: string
  theme?: string
  primaryColor?: string
  botName?: string
  greeting?: string
  placeholder?: string
  showPoweredBy?: boolean
  avatarUrl?: string
  iconStyle?: string
}

interface WidgetPreviewProps {
  config: WidgetPreviewConfig
  title?: string
  className?: string
}

// Renders the alternative (non-brand) widget icons, mirroring the widget bundle.
const PreviewIcon: React.FC<{ iconStyle: string; size: number }> = ({ iconStyle, size }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className: 'shrink-0',
  }
  switch (iconStyle) {
    case 'dots':
      return (
        <svg {...common}>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          <circle cx="8.2" cy="11.5" r="1" fill="currentColor" stroke="none" />
          <circle cx="12" cy="11.5" r="1" fill="currentColor" stroke="none" />
          <circle cx="15.8" cy="11.5" r="1" fill="currentColor" stroke="none" />
        </svg>
      )
    case 'rounded':
      return (
        <svg {...common}>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      )
    case 'spark':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none" className="shrink-0">
          <path d="M12 2l1.9 6.6L20.5 10.5l-6.6 1.9L12 19l-1.9-6.6L3.5 10.5l6.6-1.9L12 2z" />
          <circle cx="19" cy="18.5" r="1.4" />
          <circle cx="5" cy="5.5" r="1" />
        </svg>
      )
    default: // 'neutral'
      return (
        <svg {...common}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      )
  }
}

export const WidgetPreview: React.FC<WidgetPreviewProps> = ({ config, title, className = '' }) => {
  const { t } = useTranslation()

  const position = config.position || 'bottom-right'
  const theme = config.theme || 'dark'
  const primaryColor = config.primaryColor || '#006cd4'
  const botName = config.botName || t('widget.previewBotName') || 'AI Assistant'
  const greeting = config.greeting || t('widget.previewGreeting') || 'Hello! How can I help you?'
  const placeholder = config.placeholder || t('widget.previewPlaceholder') || 'Type your message...'
  const showPoweredBy = config.showPoweredBy !== false
  const avatarUrl = config.avatarUrl
  const iconStyle = config.iconStyle || 'brand'
  const isLight = theme === 'light'

  const verticalClass = position.startsWith('top') ? 'top-4' : 'bottom-4'
  const horizontalClass = position.endsWith('left') ? 'left-4 items-start' : 'right-4 items-end'
  const windowOrder = position.startsWith('top') ? 'order-2' : 'order-1'
  const bubbleOrder = position.startsWith('top') ? 'order-1' : 'order-2'

  return (
    <div className={`bg-[#12121a] border border-white/10 rounded-xl p-6 ${className}`}>
      {title && <h2 className="text-xl font-semibold mb-4">{title}</h2>}
      <div className="bg-[#0a0a0f] rounded-lg h-[430px] relative overflow-hidden">
        {/* Fake page skeleton for context */}
        <div className="absolute top-6 left-6 right-6 space-y-3 opacity-20 pointer-events-none">
          <div className="h-3 w-1/3 bg-gray-500 rounded" />
          <div className="h-2 w-2/3 bg-gray-600 rounded" />
          <div className="h-2 w-1/2 bg-gray-600 rounded" />
          <div className="h-2 w-3/5 bg-gray-600 rounded" />
        </div>

        <div className={`absolute flex flex-col gap-2 ${verticalClass} ${horizontalClass}`}>
          {/* Chat window mock (open state) */}
          <div className={`w-[260px] rounded-xl overflow-hidden shadow-2xl border border-white/10 text-left ${windowOrder}`}>
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2.5 text-white" style={{ backgroundColor: primaryColor }}>
              <div className="flex items-center gap-2 min-w-0">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                ) : iconStyle !== 'brand' ? (
                  <PreviewIcon iconStyle={iconStyle} size={18} />
                ) : (
                  <img src="https://r3converse.com/icono-chat.svg" alt="" className="w-6 h-6 shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{botName}</div>
                  <div className="text-[10px] opacity-90 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                    {t('widget.previewOnline')}
                  </div>
                </div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="opacity-80 shrink-0">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>

            {/* Messages */}
            <div className={`h-44 p-3 overflow-hidden ${isLight ? 'bg-white' : 'bg-[#181822]'}`}>
              <div className={`max-w-[85%] rounded-lg rounded-tl-none px-3 py-2 text-xs ${isLight ? 'bg-gray-100 text-gray-800' : 'bg-white/10 text-gray-200'}`}>
                {greeting}
              </div>
            </div>

            {/* Input */}
            <div className={`flex items-center gap-2 px-3 py-2.5 border-t ${isLight ? 'bg-white border-[#dee2e6]' : 'bg-[#12121a] border-white/10'}`}>
              <span className={`flex-1 text-xs px-3 py-2 rounded-full border truncate ${isLight ? 'bg-[#f8f9fa] border-[#dee2e6] text-[#71717a]' : 'bg-[#1a1a25] border-white/10 text-[#71717a]'}`}>
                {placeholder}
              </span>
              <span
                className="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0"
                style={{ backgroundColor: primaryColor }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </span>
            </div>

            {/* Powered by */}
            {showPoweredBy && (
              <div className={`text-center py-1.5 text-[10px] ${isLight ? 'bg-gray-50 text-gray-400' : 'bg-[#1f1f2b] text-gray-500'}`}>
                Powered by <span className="font-semibold">r3Converse</span>
              </div>
            )}
          </div>

          {/* Bubble launcher */}
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg ${bubbleOrder}`}
            style={{ backgroundColor: primaryColor }}
          >
            {iconStyle !== 'brand' ? (
              <PreviewIcon iconStyle={iconStyle} size={22} />
            ) : (
              <img src="https://r3converse.com/icono-chat.svg" alt="" className="w-8 h-8" />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
